import { Component, HostBinding, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SuggestAnswerService } from 'src/app/shared/services/suggest-answer.service';
import { ModalDismissRoleEnum } from 'src/app/task-process/answer-editor/send-and-popover/modal-dismiss-role.enum';
import { TagsContext } from 'src/app/task-process/shared/tags-context.class';
import { TaskProcessService } from 'src/app/task-process/shared/task-process.service';
import { environment } from 'src/environments/environment';

import { MoveToPendingModalResultEnum } from '../../../enums';
import { Tag, TagGroup, TaskState } from '../../../models/misc.model';
import { BasicService } from '../../../services/basic.service';
import { TagsService } from '../../../services/tags.service';
import { TaskService } from '../../../services/task.service';
import { MoveToPendingOrLeaveInAnsweredComponent } from '../move-to-pending-or-leave-in-answered/move-to-pending-or-leave-in-answered.component';
import { SelectTagPopoverComponent } from '../select-tag-popover/select-tag-popover.component';

@Component({
  selector: 'app-select-tag',
  templateUrl: './select-tag.component.html',
  styleUrls: ['./select-tag.component.scss'],
})
export class SelectTagComponent implements OnInit, OnDestroy {
  @Input() group: TagGroup;
  @Input() tagsContext: TagsContext;

  selectedTag: Tag;

  private subscriptions = new Subscription();

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private suggestAnswerService: SuggestAnswerService,
    private tagsService: TagsService,
    private taskProcessService: TaskProcessService,
    private taskService: TaskService
  ) {}

  @HostBinding('class.error') get hasError() {
    return this.group.isMandatoryAndMissing;
  }

  ngOnInit() {
    this.initSelectedTag();

    // If a tag is updated from the task actions modal, it needs to be updated on the task process page
    this.subscriptions.add(this.tagsContext.tagsChange$.subscribe(() => this.initSelectedTag())); // will be unsubscribed when the tagsContext is destroyed
  }

  initSelectedTag() {
    this.selectedTag = this.group.tags.find(tag => !tag.isCategory && tag.id === this.group.selectedTagId);
    if (!this.selectedTag) {
      const tag = this.group.tags.find(tag => !tag.isCategory && tag.name === 'None');
      if (tag) {
        this.group.selectedTagId = tag.id;
        this.selectedTag = tag;
      }
    }
  }

  @HostListener('click', ['$event']) async open(event) {
    const componentProps = {
      group: this.group,
      tagsContext: this.tagsContext,
    };
    const modalResult = await this.basicService.popoverWithResult(
      event,
      SelectTagPopoverComponent,
      componentProps,
      'alc-select-tag-in-popover'
    );
    if (modalResult.role === ModalDismissRoleEnum.OK && modalResult.data !== this.selectedTag) {
      this.onTagSelected(modalResult.data);
    }
  }

  private async onTagSelected(newTagValue: Tag) {
    const currentTagValue = this.selectedTag;
    let nextSteps = MoveToPendingModalResultEnum.Tag;
    // if we are routing an answered task, ask the user if they want to move the task to pending after tagging it
    // it seems it can't happen in inbox and PP
    if (this.group.isRouting && this.tagsContext.taskState === TaskState.Answered) {
      nextSteps = await this.offerToMoveToPending(newTagValue.name);
    }

    if (nextSteps !== MoveToPendingModalResultEnum.Cancel) {
      // update the selected value in the tag group
      // (this will update the ion-select when the call to tagsChanged() below triggers the subscription to tagsChange$ above)
      this.group.selectedTagId = newTagValue.id;

      // compute which tag groups, if any, should be reset to 'None'
      // because their current value depends upon the tag value that is changing
      const groupsToReset: TagGroup[] = this.tagsContext.getTagGroupsWhoseValueDependUponTagValue(currentTagValue);

      // reset them (their UI will be updated via the call to tagsChanged below)
      groupsToReset.forEach(tg => (tg.selectedTagId = tg.noneTagId));

      // update in the backend the currently changed tag and the ones that needed to be set to none
      const tagIds = [newTagValue.id, ...groupsToReset.map(tg => tg.selectedTagId)];
      this.updateTags(tagIds, currentTagValue, nextSteps);
    }
  }

  private async offerToMoveToPending(team: string): Promise<MoveToPendingModalResultEnum> {
    const modalResult = await this.basicService.popoverWithResult<MoveToPendingModalResultEnum>(
      null,
      MoveToPendingOrLeaveInAnsweredComponent,
      { team }
    );
    return modalResult.role === ModalDismissRoleEnum.OK ? modalResult.data : MoveToPendingModalResultEnum.Cancel;
  }

  /**
   * Calls the backend to update our task's tags to the given tag value ids.
   */
  private updateTags(tagIds, currentTagValue, nextSteps: MoveToPendingModalResultEnum) {
    this.tagsService.updateTagValue(this.tagsContext.taskId, tagIds, false).subscribe({
      next: () => {
        // Update mandatory but missing tags, and let interested parties (including this component) know that a tag has changed
        this.tagsContext.tagsChanged();

        // If we are changing the tag of the currently processed task,
        // and we are routing it to a team that we don't belong to, let's go back to the task list.
        const notMyTeamAnyMore = this.group.isRouting && !this.alcmeonContextService.isLoggedUserPartOfTeam(tagIds[0]);
        if (this.tagsContext.taskProcessContext && notMyTeamAnyMore) {
          this.tagsContext.taskProcessContext.backToTaskList();
        }

        if (nextSteps === MoveToPendingModalResultEnum.TagAndMoveToPending) {
          this.taskService
            .acquireTask(this.tagsContext.taskId, TaskState.Answered)
            .pipe(mergeMap(() => this.taskProcessService.moveTaskToPending(this.tagsContext.taskId)))
            .subscribe();
        }

        if (!environment.isNativeApp && this.group.isRouting) {
          this.suggestAnswerService.getAissitantConfigForTeam(this.group.selectedTagId).subscribe(resp => {
            this.suggestAnswerService.AissitantConfigForTeamBS.next(resp);
          });
        }
      },
      error: () => {
        // rollback select value in case of an error
        // (either a "real" error, or the user didn't confirm the routing to a team they don't belong to)
        this.group.selectedTagId = currentTagValue.id;
        this.selectedTag = currentTagValue;
      },
    });
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();
  }
}
